import type { AuthSource } from '@/shared/lib/auth';
import { isMockEnabled } from '@/mocks';
import { isNpsSession } from '@/shared/lib/nps-session';
import { shouldShowLeads, shouldShowOperator } from '@/shared/lib/passbook-utils';
import { firstPassbookId } from '@/shared/lib/passbook-access';
import {
  Activity,
  AtSign,
  BarChart3,
  Blocks,
  Building2,
  ClipboardList,
  Filter,
  Gift,
  Handshake,
  CreditCard,
  Gauge,
  Image,
  Layers,
  LayoutDashboard,
  LayoutTemplate,
  Mail,
  MessageSquare,
  Smartphone,
  MousePointerClick,
  Package,
  ScanLine,
  Settings,
  ShieldAlert,
  SlidersHorizontal,
  Sparkles,
  Store,
  Upload,
  UserCog,
  Users,
  Bell,
} from 'lucide-react';
import type { NavItem, PanelUser, Passbook, Role } from '@/shared/types';
import type { PageFlagKey } from '@/shared/types/passbook';

const MASTER_EMAIL = 'admin@epossible.com.br';

/** Seções da sidebar, na ordem de exibição. Permissões reusa os mesmos nomes. */
export const NAV_SECTIONS = {
  master: 'Administração',
  network: 'Minha rede',
  results: 'Resultados',
  customers: 'Público e segmentação',
  engagement: 'Relacionamento',
  loyalty: 'Gestão do Programa',
  card: 'Cartão e app',
  pointOfSale: 'Operação',
  store: 'Gestão da loja',
} as const;

type NavSection = (typeof NAV_SECTIONS)[keyof typeof NAV_SECTIONS];

const SECTION_ORDER = Object.values(NAV_SECTIONS) as NavSection[];

/** Acumula itens por seção e devolve já agrupados na ordem de SECTION_ORDER. */
function sectionedNav() {
  const buckets = new Map<NavSection, NavItem[]>();
  return {
    add(section: NavSection, item: NavItem) {
      const bucket = buckets.get(section) ?? [];
      bucket.push({ ...item, section });
      buckets.set(section, bucket);
    },
    items(): NavItem[] {
      return SECTION_ORDER.flatMap((section) => buckets.get(section) ?? []);
    },
  };
}

/** Auditoria: master sempre; os demais só com a flag efetiva do passbook. */
export function canAccessAudit(email?: string | null, passbook?: Pick<Passbook, 'hasAudit'> | null): boolean {
  if (isMasterEmail(email)) return true;
  return passbook?.hasAudit === true;
}

/** Super admin (Configurações da loja, etc.). */
export function canAccessSuperAdmin(email?: string | null): boolean {
  return isMasterEmail(email);
}

/** Master Fixe: Permissões, Custos de IA e Segmentações sem liberação. */
export function isMasterEmail(email?: string | null): boolean {
  return email?.trim().toLowerCase() === MASTER_EMAIL;
}

/** Roles cujo menu de loja tem Segmentações (as demais têm menu restrito). */
const SEGMENTATION_ROLES: ReadonlySet<Role> = new Set<Role>(['Admin', 'Client', 'Owner', 'Campaign']);

/**
 * Passbook com as flags de página que o master sobrescreveu para este usuário.
 * O master vê o passbook como ele é, para inspecionar a loja real.
 */
export function withUserPageFlags<T extends Passbook | null | undefined>(
  passbook: T,
  user?: Pick<PanelUser, 'email' | 'pageFlags'> | null,
): T {
  if (!passbook || !user?.pageFlags || isMasterEmail(user.email)) return passbook;
  const overrides = Object.entries(user.pageFlags).filter(([, value]) => typeof value === 'boolean');
  return overrides.length > 0 ? { ...passbook, ...Object.fromEntries(overrides) } : passbook;
}

/**
 * Segmentações têm flag própria (`hasSegmentations`), com override por
 * usuário já aplicado em `withUserPageFlags`. Master entra sempre; sem
 * passbook carregado nega, pra não vazar a tela num estado indefinido.
 */
export function canAccessSegmentations(email?: string | null, passbook?: Passbook | null): boolean {
  if (!passbook) return false;
  if (isMasterEmail(email)) return true;
  return passbook.hasSegmentations === true;
}

/** Catálogo de produtos — Admin (master) sempre; Client só com módulo habilitado. */
export function showProductsNav(role: Role, passbook: Passbook): boolean {
  if (role === 'Admin') return true;
  return role === 'Client' && Boolean(passbook.hasProducts);
}

/** Hub de Configurações — Admin (master) sempre; Client só com `hasConfig` no passbook. */
export function canAccessStoreConfig(role: Role, passbook: Passbook | null): boolean {
  if (role === 'Admin') return true;
  return role === 'Client' && Boolean(passbook?.hasConfig);
}
type TierPageFlag = 'hasTierSettings' | 'hasTierOverride';

/** Páginas de tiers: master sempre; Admin/Client só com o programa de tiers e a página liberados. */
function canAccessTierPage(
  flag: TierPageFlag,
  role: Role,
  email?: string | null,
  passbook?: Passbook | null,
): boolean {
  if (!passbook) return false;
  if (isMasterEmail(email)) return true;
  if (role !== 'Admin' && role !== 'Client') return false;
  return passbook.hasTiers === true && passbook[flag] === true;
}

export function canAccessTierSettings(
  role: Role,
  email?: string | null,
  passbook?: Passbook | null,
): boolean {
  return canAccessTierPage('hasTierSettings', role, email, passbook);
}

export function canAccessTierOverride(
  role: Role,
  email?: string | null,
  passbook?: Passbook | null,
): boolean {
  return canAccessTierPage('hasTierOverride', role, email, passbook);
}

/** Cadastro interno de influencer — só aparece em lojas com o cartão habilitado. */
export function canAccessInfluencerRegistration(passbook: Passbook): boolean {
  return Boolean(passbook.hasInfluencerCard);
}

const LEGACY_MANUAL_PASSBOOK_EXCEPTION = '65b14fa10142472d1519d0b7';

/** Operator + multi-store: legado esconde multi-messages nestes passbooks (app.js). */
const OPERATOR_MULTI_MESSAGES_DENYLIST = new Set([
  '673f4d58ca9cf7ff3cdaf06c',
  '67d9825fa9c9ba81b2914682',
  '65b14fa10142472d1519d0b7',
]);

/** Passbooks Bom Beef (legado app.js) — liberam WhatsApp mesmo sem flag. */
const WHATSAPP_PASSBOOK_EXCEPTIONS = new Set([
  '6389712bf911bac83ec4ff5e',
  '638e9ee3f911bac83ec4ff69',
  '6342a6c6b841ffe377a10a11',
  '63cf2c77d7e22962ee087018',
  '63cf2e4dd7e22962ee08701f',
  '63cf2f5fd7e22962ee087024',
  '63cf30b8d7e22962ee087029',
  '63cf31b4d7e22962ee08702e',
  '63cf334ad7e22962ee087033',
  '63d196edd7e22962ee087038',
  '63d19829d7e22962ee08703d',
  '63d19902d7e22962ee087042',
  '63d33f9f5d6c0fdb8e26f200',
  '63d340c25d6c0fdb8e26f205',
  '63d3416e5d6c0fdb8e26f20a',
  '63d342935d6c0fdb8e26f214',
  '63d342075d6c0fdb8e26f20f',
  '63d347fb5d6c0fdb8e26f226',
  '63d47c385d6c0fdb8e26f22d',
  '63d47cba5d6c0fdb8e26f232',
  '63d47d445d6c0fdb8e26f237',
  '63d47ddb5d6c0fdb8e26f23c',
  '63d47ec15d6c0fdb8e26f243',
  '63d47f515d6c0fdb8e26f248',
  '63d47ff95d6c0fdb8e26f24d',
  '63d5198f5d6c0fdb8e26f252',
  '63d51a3b5d6c0fdb8e26f257',
  '63d51b6a5d6c0fdb8e26f261',
  '63d520ad5d6c0fdb8e26f271',
  '63d81c40b095f639af7d6d6a',
  '63d81cc8b095f639af7d6d6f',
  '63d81e59b095f639af7d6d7e',
  '63d81f2db095f639af7d6d83',
  '63d86cdab095f639af7d6d88',
  '63d86d5db095f639af7d6d8d',
  '63d86e6bb095f639af7d6d97',
  '63d86ef1b095f639af7d6d9c',
  '63d86f85b095f639af7d6da1',
  '63d8700eb095f639af7d6da6',
  '63d901c4b095f639af7d6dad',
  '63d90264b095f639af7d6db2',
  '63d97d6bb095f639af7d6db7',
  '63d97dddb095f639af7d6dbc',
  '63d97e56b095f639af7d6dc1',
  '63d97ed4b095f639af7d6dc6',
  '63d97f65b095f639af7d6dcb',
  '63d980f7b095f639af7d6dda',
  '63d98187b095f639af7d6ddf',
  '63da7a97b095f639af7d6dff',
  '63da7b3eb095f639af7d6e04',
  '63da7c02b095f639af7d6e09',
  '63da9b74b095f639af7d6e0f',
  '63da9bf5b095f639af7d6e14',
  '63da9c82b095f639af7d6e19',
  '63da9d0bb095f639af7d6e1e',
  '63da9d90b095f639af7d6e23',
  '63da9e04b095f639af7d6e28',
  '63da9e72b095f639af7d6e2d',
  '63e5a6e5ff0aa73ff48246fc',
  '640fba7f57314e4c9ec4ef17',
  '640fbc5557314e4c9ec4ef1c',
  '640fbd3057314e4c9ec4ef21',
  '647beb8fdfa8fdfb3501e534',
  '63d51cf55d6c0fdb8e26f270',
  '63d86dddb095f639af7d6d92',
  '63d81de0b095f639af7d6d79',
  '640fb93657314e4c9ec4ef12',
]);

/** Operator: Campanhas Ativas (multi-messages) — só com hasMultiStore e fora da denylist. */
export function showOperatorActiveCampaigns(passbook: Passbook): boolean {
  return Boolean(passbook.hasMultiStore) && !OPERATOR_MULTI_MESSAGES_DENYLIST.has(passbook._id);
}

/** Disparos WhatsApp (MessageWP) — item do submenu Campanhas. */
export function showWhatsappCampaigns(role: Role, passbook: Passbook): boolean {
  // Operator/Owner no path autenticado do legado não recebem WhatsApp no menu.
  // Owner usa /owner/campanhas; a lista Bom Beef é só para Client da loja.
  if (role === 'Operator' || role === 'Owner') return false;
  return Boolean(passbook.hasSimpleCampaignWhatsApp) || WHATSAPP_PASSBOOK_EXCEPTIONS.has(passbook._id);
}

/** Mensagens Gerais / Campanhas Ativas. */
export function showManualCampaigns(role: Role, passbook: Passbook): boolean {
  if (role === 'Owner') return false;
  if (passbook._id === LEGACY_MANUAL_PASSBOOK_EXCEPTION && role !== 'Operator') return true;
  if (isMockEnabled() && passbook.hasCampaign !== false && role !== 'Operator') return true;
  if (role === 'Operator') return showOperatorActiveCampaigns(passbook);
  if (role === 'Admin') return true;
  if (passbook.hasGiftcard) return false;
  if (passbook.hasCampaignAdvanced) return false;
  return passbook.hasCampaign !== false;
}

/** Campanhas automatizadas da loja (não é o módulo do Owner). */
export function showAutomatedCampaigns(role: Role, passbook: Passbook): boolean {
  if (role === 'Owner') return false;
  if (!passbook.hasCampaignAdvanced) return false;
  if (role === 'Admin' || role === 'Campaign' || role === 'Client') return true;
  return false;
}

/** Módulo de campanha do franqueador — flag no user, não no passbook. */
export function showOwnerAutomatedCampaigns(
  user: Pick<PanelUser, 'role' | 'hasCampaignAdvanced'>,
): boolean {
  return user.role === 'Owner' && user.hasCampaignAdvanced === true;
}

export function canAccessOwnerCampaigns(
  user: Pick<PanelUser, 'role' | 'hasCampaignAdvanced'>,
): boolean {
  return showOwnerAutomatedCampaigns(user);
}

/** Upload Metabase — Admin e Client (com flag no passbook). */
export function showUploadCampaigns(role: Role, passbook: Passbook): boolean {
  return (role === 'Admin' || role === 'Client') && Boolean(passbook.hasCampaignMetabase);
}

/** Campanhas do motor de fidelidade — Admin e Client (com flag no passbook). */
export function showLoyaltyCampaigns(role: Role, passbook: Passbook): boolean {
  return (role === 'Admin' || role === 'Client') && Boolean(passbook.hasLoyaltyCampaigns);
}

/**
 * Indique e Ganhe (Member Get Member) — Admin, Client e Owner com o módulo
 * (`hasMemberGetMember`) ligado no passbook. A campanha em si só o master edita
 * (`canConfigureMemberGetMember`).
 */
export function showMemberGetMember(role: Role, passbook: Passbook): boolean {
  return (role === 'Admin' || role === 'Client' || role === 'Owner') && Boolean(passbook.hasMemberGetMember);
}

/** Criar/editar a campanha do Indique e Ganhe: só o master, e só com o módulo ligado. */
export function canConfigureMemberGetMember(email?: string | null, passbook?: Passbook | null): boolean {
  return isMasterEmail(email) && Boolean(passbook?.hasMemberGetMember);
}

/** E-mail marketing é um módulo à parte. Só entra com a flag do passbook ligada. */
export function showEmailMarketing(role: Role, passbook: Passbook): boolean {
  return (role === 'Admin' || role === 'Client' || role === 'Owner') && Boolean(passbook.hasEmailMarketing);
}

/** SMS marketing é outro módulo. Não acompanha a flag de e-mail. */
export function showSmsMarketing(role: Role, passbook: Passbook): boolean {
  return (role === 'Admin' || role === 'Client' || role === 'Owner') && Boolean(passbook.hasSmsMarketing);
}

function emailMarketingNav(base: string): NavItem {
  const root = `${base}/email-marketing`;
  return {
    name: 'E-mail',
    to: `${root}/campanhas`,
    icon: Mail,
    children: [
      { name: 'Campanhas', to: `${root}/campanhas`, icon: Mail },
      { name: 'Templates', to: `${root}/templates`, icon: LayoutTemplate },
      { name: 'Envio', to: `${root}/configuracao`, icon: AtSign },
    ],
  };
}

function smsMarketingNav(base: string): NavItem {
  const root = `${base}/sms-marketing`;
  return {
    name: 'SMS',
    to: `${root}/campanhas`,
    icon: Smartphone,
    children: [
      { name: 'Campanhas', to: `${root}/campanhas`, icon: MessageSquare },
      { name: 'Envio', to: `${root}/configuracao`, icon: AtSign },
    ],
  };
}

function pushMarketingNav(base: string): NavItem {
  const root = `${base}/push-marketing`;
  return {
    name: 'Push',
    to: `${root}/campanhas`,
    icon: Bell,
    children: [
      { name: 'Campanhas', to: `${root}/campanhas`, icon: Bell },
      { name: 'Envio', to: `${root}/configuracao`, icon: Settings },
    ],
  };
}

function loyaltyCampaignsItem(base: string): NavItem {
  return { name: 'Campanhas de fidelidade', to: `${base}/campanhas/fidelidade`, icon: Gift };
}

/**
 * Grupo "Campanhas". Na sidebar a de fidelidade mora em Programa de
 * fidelidade (`includeLoyalty: false`); o redirect de `/campanhas` considera todas.
 */
function buildCampaignNav(
  base: string,
  role: Role,
  passbook: Passbook,
  { includeLoyalty = true }: { includeLoyalty?: boolean } = {},
): NavItem[] {
  const items: NavItem[] = [];

  // Operator: só a mensagem no cartão (multi-messages), sem Upload.
  if (role === 'Operator') {
    if (showOperatorActiveCampaigns(passbook)) {
      items.push({ name: 'Mensagem no cartão', to: `${base}/campanhas/manual`, icon: MessageSquare });
    }
    return items;
  }

  if (showManualCampaigns(role, passbook)) {
    items.push({ name: 'Mensagem no cartão', to: `${base}/campanhas/manual`, icon: MessageSquare });
  }
  if (showUploadCampaigns(role, passbook)) {
    items.push({ name: 'Upload de lista', to: `${base}/campanhas/upload`, icon: Upload });
  }
  if (includeLoyalty && showLoyaltyCampaigns(role, passbook)) {
    items.push({ ...loyaltyCampaignsItem(base), name: 'Fidelidade' });
  }
  return items;
}

/** Primeiro path de campanha permitido (redirect de `/campanhas`). */
export function firstAllowedCampaignPath(role: Role, passbook: Passbook, base: string): string | null {
  return buildCampaignNav(base, role, passbook)[0]?.to ?? null;
}

/**
 * Páginas que não existem como flag do passbook.
 * Sem override do usuário, o perfil Base usa este padrão.
 */
const BASE_FLAG_DEFAULTS: Partial<Record<PageFlagKey, boolean>> = {
  hasDashboard: true,
  hasLeads: true,
  hasOperator: true,
};

/** Flag efetiva para o perfil Base: override do usuário (já mesclado) vence o padrão. */
export function baseFlagOn(passbook: Passbook | null | undefined, key: PageFlagKey): boolean {
  const value = passbook?.[key];
  if (typeof value === 'boolean') return value;
  return BASE_FLAG_DEFAULTS[key] ?? false;
}

type SectionedNav = ReturnType<typeof sectionedNav>;

/** Menu da loja do perfil Base: cada item existe só quando a página está liberada. */
function appendBaseNavigation(nav: SectionedNav, base: string, passbook: Passbook) {
  const S = NAV_SECTIONS;
  const on = (key: PageFlagKey) => baseFlagOn(passbook, key);

  if (on('hasDashboard')) nav.add(S.results, { name: 'Painel', to: base, icon: LayoutDashboard });
  if (on('hasLeads')) nav.add(S.customers, { name: 'Leads', to: `${base}/leads`, icon: Users });
  if (on('hasAudit')) nav.add(S.store, { name: 'Auditoria', to: `${base}/leads/auditoria`, icon: ShieldAlert });
  if (on('hasSegmentations')) {
    nav.add(S.customers, { name: 'Audiência', to: `${base}/segmentacoes`, icon: Filter });
  }

  if (on('hasCampaign')) {
    nav.add(S.engagement, { name: 'Mensagem no cartão', to: `${base}/campanhas/manual`, icon: MessageSquare });
  }
  if (on('hasCampaignMetabase')) {
    nav.add(S.engagement, { name: 'Upload de lista', to: `${base}/campanhas/upload`, icon: Upload });
  }
  if (on('hasCampaignAdvanced')) nav.add(S.engagement, pushMarketingNav(base));

  if (on('hasLoyaltyCampaigns')) nav.add(S.loyalty, loyaltyCampaignsItem(base));
  if (on('hasMemberGetMember')) {
    nav.add(S.loyalty, { name: 'Indique e ganhe', to: `${base}/indique-e-ganhe`, icon: Handshake });
  }
  if (on('hasProducts')) {
    nav.add(S.loyalty, { name: 'Produtos', to: `${base}/produtos`, icon: Package });
    nav.add(S.loyalty, { name: 'Resgates', to: `${base}/produtos/resgates`, icon: Gift });
  }
  if (on('hasInfluencerCard')) {
    nav.add(S.loyalty, { name: 'Parceiros', to: `${base}/influencer`, icon: Sparkles });
  }
  if (on('hasTierSettings')) nav.add(S.loyalty, { name: 'Tiers', to: `${base}/tiers`, icon: Layers });
  if (on('hasTierOverride')) {
    nav.add(S.loyalty, { name: 'Ajustar cliente', to: `${base}/tiers/ajustar-cliente`, icon: UserCog });
  }
  if (on('hasBanners')) nav.add(S.card, { name: 'Banners', to: `${base}/banners`, icon: Image });
  if (on('hasFabButtons')) {
    nav.add(S.card, { name: 'Botões flutuantes', to: `${base}/fab-buttons`, icon: MousePointerClick });
  }
  if (on('hasOperator')) nav.add(S.pointOfSale, { name: 'Operador', to: `${base}/operador`, icon: ScanLine });
  if (on('hasConfig')) nav.add(S.store, { name: 'Configurações', to: `${base}/configuracoes`, icon: Settings });
}

function baseHasAnyCampaign(passbook?: Passbook | null): boolean {
  return (
    baseFlagOn(passbook, 'hasCampaign') ||
    baseFlagOn(passbook, 'hasSimpleCampaignWhatsApp') ||
    baseFlagOn(passbook, 'hasCampaignAdvanced') ||
    baseFlagOn(passbook, 'hasCampaignMetabase')
  );
}

/** Rotas de loja do perfil Base — a mesma lista que monta o menu. */
export function canAccessBasePath(path: string, passbook?: Passbook | null): boolean {
  if (path === '/' || path === '') return baseFlagOn(passbook, 'hasDashboard');
  if (path.startsWith('/leads/auditoria') || path.startsWith('/leads/bloqueados')) {
    return baseFlagOn(passbook, 'hasAudit');
  }
  if (path.startsWith('/leads')) return baseFlagOn(passbook, 'hasLeads');
  if (path.startsWith('/segmentacoes')) return baseFlagOn(passbook, 'hasSegmentations');
  if (path === '/campanhas' || path === '/campanhas/') return baseHasAnyCampaign(passbook);
  if (path.startsWith('/campanhas/manual')) return baseFlagOn(passbook, 'hasCampaign');
  if (path.startsWith('/campanhas/whatsapp') || path.startsWith('/campanhas-whatsapp')) {
    return baseFlagOn(passbook, 'hasSimpleCampaignWhatsApp');
  }
  if (
    path.startsWith('/push-marketing') ||
    path.startsWith('/campanhas/automatizadas') ||
    path.startsWith('/automacoes')
  ) {
    return baseFlagOn(passbook, 'hasCampaignAdvanced');
  }
  if (path.startsWith('/campanhas/upload')) return baseFlagOn(passbook, 'hasCampaignMetabase');
  if (path.startsWith('/campanhas/fidelidade')) return baseFlagOn(passbook, 'hasLoyaltyCampaigns');
  if (path.startsWith('/indique-e-ganhe/campanha')) return false;
  if (path.startsWith('/indique-e-ganhe')) return baseFlagOn(passbook, 'hasMemberGetMember');
  if (path.startsWith('/produtos')) return baseFlagOn(passbook, 'hasProducts');
  if (path.startsWith('/influencer')) return baseFlagOn(passbook, 'hasInfluencerCard');
  if (path.startsWith('/tiers/ajustar-cliente')) return baseFlagOn(passbook, 'hasTierOverride');
  if (path.startsWith('/tiers')) return baseFlagOn(passbook, 'hasTierSettings');
  if (path.startsWith('/banners')) return baseFlagOn(passbook, 'hasBanners');
  if (path.startsWith('/fab-buttons')) return baseFlagOn(passbook, 'hasFabButtons');
  if (path.startsWith('/operador')) return baseFlagOn(passbook, 'hasOperator');
  if (path.startsWith('/configuracoes')) return baseFlagOn(passbook, 'hasConfig');
  return false;
}

const BASE_LANDING: Array<{ suffix: string; key: PageFlagKey }> = [
  { suffix: '', key: 'hasDashboard' },
  { suffix: '/leads', key: 'hasLeads' },
  { suffix: '/segmentacoes', key: 'hasSegmentations' },
  { suffix: '/campanhas/manual', key: 'hasCampaign' },
  { suffix: '/push-marketing/campanhas', key: 'hasCampaignAdvanced' },
  { suffix: '/campanhas/upload', key: 'hasCampaignMetabase' },
  { suffix: '/campanhas/fidelidade', key: 'hasLoyaltyCampaigns' },
  { suffix: '/indique-e-ganhe', key: 'hasMemberGetMember' },
  { suffix: '/produtos', key: 'hasProducts' },
  { suffix: '/influencer', key: 'hasInfluencerCard' },
  { suffix: '/tiers', key: 'hasTierSettings' },
  { suffix: '/tiers/ajustar-cliente', key: 'hasTierOverride' },
  { suffix: '/banners', key: 'hasBanners' },
  { suffix: '/fab-buttons', key: 'hasFabButtons' },
  { suffix: '/operador', key: 'hasOperator' },
  { suffix: '/leads/auditoria', key: 'hasAudit' },
  { suffix: '/configuracoes', key: 'hasConfig' },
];

/** Primeira página liberada do perfil Base. `null` quando o master bloqueou todas. */
export function firstBasePath(idPassbook: string, passbook?: Passbook | null): string | null {
  const hit = BASE_LANDING.find((page) => baseFlagOn(passbook, page.key));
  return hit ? `/loja/${idPassbook}${hit.suffix}` : null;
}

/**
 * Deriva o menu a partir da role + feature flags do passbook.
 * Espelha setMenuCard / authenticatedToken de legacy/dash/app.js.
 */
export function buildNavigation(
  user: PanelUser,
  idPassbook?: string,
  passbook?: Passbook | null,
  npsIsAdmin?: boolean,
  authSource?: AuthSource | null,
): NavItem[] {
  const role = user.role;

  if (isNpsSession(authSource) || role === 'Nps') {
    const items: NavItem[] = [
      { name: 'Visão Geral', to: '/nps', icon: Gauge },
      { name: 'Pesquisas', to: '/nps/pesquisas', icon: ClipboardList },
      { name: 'Integrações', to: '/nps/integracoes', icon: Blocks },
    ];
    if (npsIsAdmin !== false) items.push({ name: 'Usuários', to: '/nps/usuarios', icon: Users });
    items.push({ name: 'Perfil', to: '/nps/perfil', icon: UserCog });
    return items;
  }

  const nav = sectionedNav();
  const S = NAV_SECTIONS;

  if (role === 'Employee') return nav.items();

  if (role === 'Admin') {
    nav.add(S.master, { name: 'Clientes', to: '/clientes', icon: Building2 });
    // Módulos (/modulos) fica fora do menu; a rota continua acessível pela URL.
    // Agenda de campanhas, log de disparos e custos de IA são abas de Observabilidade.
    // Usuários e IA de cada cliente ficam no hub (/clientes/:id).
    nav.add(S.master, { name: 'Observabilidade', to: '/observabilidade', icon: Activity });
  }
  // Plataforma: e-mails e pushes do sistema. Só o master.
  if (role === 'Admin' && isMasterEmail(user.email)) {
    nav.add(S.master, { name: 'Plataforma', to: '/plataforma', icon: SlidersHorizontal });
  }

  if (role === 'Owner') {
    nav.add(S.network, { name: 'Visão Geral', to: '/owner', icon: BarChart3 });
    nav.add(S.network, { name: 'Minhas Lojas', to: '/owner/lojas', icon: Store });
    if (showOwnerAutomatedCampaigns(user)) {
      nav.add(S.network, {
        name: 'Push',
        to: '/owner/campanhas',
        icon: Bell,
        children: [
          { name: 'Campanhas', to: '/owner/campanhas', icon: Bell },
          { name: 'Envio', to: '/owner/campanhas/configuracao', icon: Settings },
        ],
      });
    }
  }

  // Sem passbook carregado: Campaign/Client/Admin com flag no user ainda apontam
  // para automatizadas da loja. Owner já ganhou /owner/campanhas acima.
  if (!idPassbook || !passbook) {
    if (role === 'Owner') return nav.items();
    const storeId = firstPassbookId(user) ?? idPassbook;
    const wantsAdvanced =
      role === 'Campaign' ||
      (Boolean(user.hasCampaignAdvanced) && (role === 'Client' || role === 'Admin'));
    if (wantsAdvanced && storeId) {
      nav.add(S.engagement, {
        name: 'Push',
        to: `/loja/${storeId}/push-marketing/campanhas`,
        icon: Bell,
      });
    }
    return nav.items();
  }

  const base = `/loja/${idPassbook}`;

  // Base não herda menu de Admin, Client, Operator etc. Só entra o que o master liberou.
  if (role === 'Base') {
    appendBaseNavigation(nav, base, passbook);
    return nav.items();
  }

  const campaignNav = buildCampaignNav(base, role, passbook, { includeLoyalty: false });
  const segmentations = canAccessSegmentations(user.email, passbook);

  // Validator: só tela de operador (legado /verify)
  if (role === 'Validator') {
    nav.add(S.pointOfSale, { name: 'Operador', to: `${base}/operador`, icon: ScanLine });
    return nav.items();
  }

  // Campaign: só campanhas automatizadas (+ upload se flag)
  if (role === 'Campaign') {
    for (const item of campaignNav) nav.add(S.engagement, item);
    if (showAutomatedCampaigns(role, passbook)) nav.add(S.engagement, pushMarketingNav(base));
    if (segmentations) {
      nav.add(S.customers, { name: 'Audiência', to: `${base}/segmentacoes`, icon: Filter });
    }
    return nav.items();
  }

  // Manager: só painel
  if (role === 'Manager') {
    nav.add(S.results, { name: 'Painel', to: base, icon: LayoutDashboard });
    return nav.items();
  }

  // Operator: Painel (se Metabase) + leads + campanhas multi + operador
  if (role === 'Operator') {
    if (passbook.metabase && passbook.metabase.length > 0) {
      nav.add(S.results, { name: 'Painel', to: base, icon: LayoutDashboard });
    }
    if (shouldShowLeads(passbook, role)) {
      nav.add(S.customers, { name: 'Leads', to: `${base}/leads`, icon: Users });
    }
    for (const item of campaignNav) nav.add(S.engagement, item);
    if (shouldShowOperator(passbook)) {
      nav.add(S.pointOfSale, { name: 'Operador', to: `${base}/operador`, icon: ScanLine });
    }
    return nav.items();
  }

  // Admin / Client / Owner dentro da loja
  nav.add(S.results, { name: 'Painel', to: base, icon: LayoutDashboard });

  if (shouldShowLeads(passbook, role)) {
    nav.add(S.customers, { name: 'Leads', to: `${base}/leads`, icon: Users });
    if (canAccessAudit(user.email, passbook)) {
      nav.add(S.store, { name: 'Auditoria', to: `${base}/leads/auditoria`, icon: ShieldAlert });
    }
  }
  if (segmentations) {
    nav.add(S.customers, { name: 'Audiência', to: `${base}/segmentacoes`, icon: Filter });
  }

  for (const item of campaignNav) nav.add(S.engagement, item);
  if (showAutomatedCampaigns(role, passbook)) nav.add(S.engagement, pushMarketingNav(base));
  if (showEmailMarketing(role, passbook)) nav.add(S.engagement, emailMarketingNav(base));
  if (showSmsMarketing(role, passbook)) nav.add(S.engagement, smsMarketingNav(base));

  if (showLoyaltyCampaigns(role, passbook)) nav.add(S.loyalty, loyaltyCampaignsItem(base));
  if (showMemberGetMember(role, passbook)) {
    nav.add(S.loyalty, { name: 'Indique e ganhe', to: `${base}/indique-e-ganhe`, icon: Handshake });
  }
  if (showProductsNav(role, passbook)) {
    nav.add(S.loyalty, { name: 'Produtos', to: `${base}/produtos`, icon: Package });
    nav.add(S.loyalty, { name: 'Resgates', to: `${base}/produtos/resgates`, icon: Gift });
  }
  if (canAccessInfluencerRegistration(passbook)) {
    nav.add(S.loyalty, { name: 'Parceiros', to: `${base}/influencer`, icon: Sparkles });
  }
  if (canAccessTierSettings(role, user.email, passbook)) {
    nav.add(S.loyalty, { name: 'Tiers', to: `${base}/tiers`, icon: Layers });
  }
  if (canAccessTierOverride(role, user.email, passbook)) {
    nav.add(S.loyalty, {
      name: 'Ajustar cliente',
      to: `${base}/tiers/ajustar-cliente`,
      icon: UserCog,
    });
  }

  if (canAccessSuperAdmin(user.email)) {
    nav.add(S.card, { name: 'Verso do cartão', to: `${base}/verso-cartao`, icon: CreditCard });
  }
  if (role === 'Admin') {
    nav.add(S.card, { name: 'Banners', to: `${base}/banners`, icon: Image });
    nav.add(S.card, { name: 'Botões flutuantes', to: `${base}/fab-buttons`, icon: MousePointerClick });
  } else if (passbook._id === LEGACY_MANUAL_PASSBOOK_EXCEPTION) {
    nav.add(S.card, { name: 'Banners', to: `${base}/banners`, icon: Image });
  }

  if (shouldShowOperator(passbook) || role === 'Admin') {
    nav.add(S.pointOfSale, { name: 'Operador', to: `${base}/operador`, icon: ScanLine });
  }

  if (canAccessSuperAdmin(user.email)) {
    nav.add(S.store, { name: 'Metabase', to: `${base}/metabase`, icon: BarChart3 });
  }
  if (canAccessStoreConfig(role, passbook)) {
    nav.add(S.store, { name: 'Configurações', to: `${base}/configuracoes`, icon: Settings });
  }
  // Configuração do cliente (recursos, páginas, usuários, IA) mora em /clientes/:id;
  // na inspeção o atalho é o botão Configurar do header, não um item do menu.

  return nav.items();
}

function canAccessCampaignPath(role: Role, path: string, passbook: Passbook): boolean {
  if (path === '/campanhas' || path === '/campanhas/') {
    return firstAllowedCampaignPath(role, passbook, '') != null;
  }
  if (path.startsWith('/campanhas/manual')) return showManualCampaigns(role, passbook);
  if (path.startsWith('/campanhas/whatsapp')) return showWhatsappCampaigns(role, passbook);
  if (path.startsWith('/campanhas/automatizadas') || path.startsWith('/automacoes') || path.startsWith('/push-marketing')) {
    return showAutomatedCampaigns(role, passbook);
  }
  if (path.startsWith('/campanhas/upload')) return showUploadCampaigns(role, passbook);
  if (path.startsWith('/campanhas/fidelidade')) return showLoyaltyCampaigns(role, passbook);
  if (path.startsWith('/campanhas-whatsapp')) return showWhatsappCampaigns(role, passbook);
  return path.startsWith('/campanhas');
}

/** Rotas de loja permitidas por role (guards). */
export function canAccessTenantPath(
  role: Role,
  pathname: string,
  passbook?: Passbook | null,
  email?: string | null,
): boolean {
  const path = pathname.replace(/\/loja\/[^/]+/, '') || '/';
  if (role === 'Employee') return false;

  if (path.startsWith('/permissoes')) return isMasterEmail(email);

  if (role === 'Base') return canAccessBasePath(path, passbook);

  // Segmentações: decide antes de qualquer regra de role, senão Admin/Client
  // entrariam pela URL sem a liberação.
  if (path.startsWith('/segmentacoes')) {
    return SEGMENTATION_ROLES.has(role) && canAccessSegmentations(email, passbook);
  }

  if (role === 'Validator') {
    return path === '/operador' || path.startsWith('/operador/');
  }

  if (role === 'Campaign') {
    if (!passbook) return path.startsWith('/campanhas');
    return canAccessCampaignPath(role, path, passbook);
  }

  if (role === 'Manager') {
    return path === '/' || path === '';
  }

  if (role === 'Operator') {
    if (
      path.startsWith('/banners') ||
      path.startsWith('/fab-buttons') ||
      path.startsWith('/configuracoes') ||
      path.startsWith('/tiers') ||
      path.startsWith('/verso-cartao') ||
      path.startsWith('/metabase') ||
      path.startsWith('/produtos') ||
      path.startsWith('/leads/auditoria') ||
      path.startsWith('/leads/bloqueados') ||
      path.startsWith('/indique-e-ganhe') ||
      path.startsWith('/email-marketing') ||
      path.startsWith('/sms-marketing') ||
      path.startsWith('/push-marketing')
    ) {
      return false;
    }
    // Painel só com Metabase no passbook (legado authenticatedToken).
    if (path === '/' || path === '') {
      return Boolean(passbook?.metabase && passbook.metabase.length > 0);
    }
    if (path.startsWith('/operador') && passbook && !shouldShowOperator(passbook)) return false;
    if (path.startsWith('/campanhas') || path.startsWith('/automacoes') || path.startsWith('/campanhas-whatsapp')) {
      if (!passbook) return false;
      return canAccessCampaignPath(role, path, passbook);
    }
    return true;
  }

  // Admin / Client / Owner
  if (path.startsWith('/email-marketing/sms')) return false;
  if (path.startsWith('/email-marketing')) {
    return Boolean(passbook && showEmailMarketing(role, passbook));
  }
  if (path.startsWith('/sms-marketing')) {
    return Boolean(passbook && showSmsMarketing(role, passbook));
  }
  if (path.startsWith('/push-marketing')) {
    return Boolean(passbook && showAutomatedCampaigns(role, passbook));
  }
  if (path.startsWith('/indique-e-ganhe/campanha')) {
    return canConfigureMemberGetMember(email, passbook);
  }
  if (path.startsWith('/indique-e-ganhe')) {
    return Boolean(passbook && showMemberGetMember(role, passbook));
  }
  if (path.startsWith('/produtos')) {
    return Boolean(passbook && showProductsNav(role, passbook));
  }
  if (path.startsWith('/influencer')) {
    return Boolean(passbook && canAccessInfluencerRegistration(passbook));
  }
  if (path.startsWith('/tiers/ajustar-cliente')) {
    return canAccessTierOverride(role, email, passbook);
  }
  if (path.startsWith('/tiers')) {
    return canAccessTierSettings(role, email, passbook);
  }
  if (path.startsWith('/leads/auditoria') || path.startsWith('/leads/bloqueados')) {
    return canAccessAudit(email, passbook);
  }
  if (path.startsWith('/configuracoes')) {
    return canAccessStoreConfig(role, passbook ?? null);
  }
  if (path.startsWith('/verso-cartao') || path.startsWith('/metabase')) {
    return canAccessSuperAdmin(email);
  }
  if ((path.startsWith('/banners') || path.startsWith('/fab-buttons')) && role !== 'Admin') {
    if (passbook?._id === LEGACY_MANUAL_PASSBOOK_EXCEPTION && path.startsWith('/banners')) return true;
    return false;
  }
  if (
    passbook &&
    (path.startsWith('/campanhas') || path.startsWith('/automacoes') || path.startsWith('/campanhas-whatsapp'))
  ) {
    return canAccessCampaignPath(role, path, passbook);
  }

  return true;
}
